import React, { useMemo, useState } from "react";
import { Hourglass, MessageSquare } from "lucide-react";
import { Badge } from "@/dentlab/src/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import {
	getLocalStorage,
	setLocalStorage,
} from "@/lib/utils/local-storage";

export interface UntouchedJob {
	id: number;
	code: string | null;
	title: string | null;
	client_id: string;
	client_name: string;
	patient_first_name: string | null;
	patient_last_name: string | null;
	from_request: boolean;
}

interface UntouchedJobsWidgetProps {
	jobs: UntouchedJob[];
	onJobClick: (job: UntouchedJob) => void;
	className?: string;
}

const ONLY_FROM_REQUEST_KEY = "dentlab-untouched-jobs-only-from-request";

export const UntouchedJobsWidget: React.FC<UntouchedJobsWidgetProps> = ({
	jobs,
	onJobClick,
	className,
}) => {
	const patientNameFn = usePatientStore((state) => state.patientName);
	const [onlyFromRequest, setOnlyFromRequest] = useState(() =>
		getLocalStorage<boolean>(ONLY_FROM_REQUEST_KEY, false)
	);

	const visibleJobs = useMemo(
		() => (onlyFromRequest ? jobs.filter((j) => j.from_request) : jobs),
		[jobs, onlyFromRequest]
	);

	const handleToggleOnlyFromRequest = () => {
		const next = !onlyFromRequest;
		setOnlyFromRequest(next);
		setLocalStorage(ONLY_FROM_REQUEST_KEY, next);
	};

	return (
		<div
			className={cn(
				"bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col",
				className
			)}
			data-testid="untouched-jobs-widget"
		>
			<div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 flex-shrink-0">
				<Hourglass className="h-4 w-4 text-gray-500" />
				<h3 className="text-sm font-semibold text-gray-900">
					Unbearbeitete Aufträge
				</h3>
				{visibleJobs.length > 0 && (
					<Badge
						variant="secondary"
						className="h-5 min-w-[20px] px-1.5 text-xs"
					>
						{visibleJobs.length}
					</Badge>
				)}
				<button
					className={cn(
						"ml-auto text-xs px-2 py-0.5 rounded-full border transition-colors",
						onlyFromRequest
							? "bg-primary text-primary-foreground border-primary"
							: "bg-white text-gray-500 border-gray-300 hover:border-gray-400"
					)}
					onClick={handleToggleOnlyFromRequest}
					aria-label="Nur aus Auftragsanfragen filtern"
					data-testid="untouched-jobs-only-from-request-filter"
				>
					Nur aus Anfragen
				</button>
			</div>

			<div className="border-t border-gray-200" />

			{visibleJobs.length === 0 ? (
				<div className="flex-1 flex items-center justify-center pb-3">
					<div className="text-center">
						<Hourglass className="h-6 w-6 mx-auto mb-1 text-gray-300" />
						<p className="text-xs text-gray-400">
							{onlyFromRequest
								? "Keine unbearbeiteten Anfragen"
								: "Keine unbearbeiteten Aufträge"}
						</p>
					</div>
				</div>
			) : (
				<div className="flex-1 min-h-0 overflow-y-auto">
					{visibleJobs.map((job) => {
						const patientName =
							job.patient_first_name || job.patient_last_name
								? patientNameFn({
										patient_id: "",
										first_name: job.patient_first_name ?? "",
										last_name: job.patient_last_name ?? "",
										code: "",
										title: "",
									})
								: "";

						return (
							<div
								key={job.id}
								className="px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
								onClick={() => onJobClick(job)}
								data-testid={`untouched-job-${job.id}`}
								aria-label={`Auftrag öffnen: ${job.code || ""} ${job.title || ""}`}
							>
								<div className="flex items-center gap-2">
									<span className="text-xs font-medium text-gray-500 flex-shrink-0">
										{job.code}
									</span>
									<span className="text-sm font-semibold text-gray-900 truncate">
										{job.title}
									</span>
									{job.from_request && (
										<Tooltip>
											<TooltipTrigger asChild>
												<MessageSquare
													className="h-3.5 w-3.5 text-primary flex-shrink-0"
													aria-label="Aus Auftragsanfrage"
												/>
											</TooltipTrigger>
											<TooltipContent side="top">
												Aus Auftragsanfrage
											</TooltipContent>
										</Tooltip>
									)}
									{patientName && (
										<span className="text-sm font-medium text-gray-700 truncate ml-auto flex-shrink-0">
											{patientName}
										</span>
									)}
								</div>
								{job.client_name && (
									<div className="text-xs text-gray-500 truncate">
										{job.client_name}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
